import type { ICreateRestaurantRequest } from '@/api/services/restaurants.service';
import type { TSlugState } from '@/composition/store-setup/use-slug-availability';
import type { IStoreSetupDraft } from '@/composition/store-setup/use-store-setup-draft';
import type { TBusinessType } from '@/interfaces/IStoreSetup';
import { CURRENCIES } from '@/modules/currencies';
import type { IPickerOption } from './location-step';
import { currencyPluralName } from './money';
import { weekToRows } from './opening-hours';
import { currencyForCountry } from './store-country';
import { SLUG_MIN, isValidFlowSlug, slugBase } from './store-slug';

/**
 * Pure rules of «Negocio» (plan §4.2): the name, the link that follows it,
 * the currency list, what blocks «Crear mi tienda», the `POST /restaurants`
 * body and the `store_created` event.
 */

export const NAME_MIN = 2;
export const NAME_MAX = 64;
/** Suggestion chips under a taken link. */
export const MAX_SLUG_SUGGESTIONS = 3;

/** Trimmed, 2 to 64 characters (the API's `NAME_INVALID` rule). */
export function isValidStoreName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= NAME_MIN && trimmed.length <= NAME_MAX;
}

/** The link while it follows the name (D4): empty until there is a name. */
export function followedSlug(name: string, city: string | null): string {
  return name.trim() ? slugBase(name, city) : '';
}

/** The link that `POST /restaurants` receives: the owner's once edited, else the one following the name. */
export function draftSlug(draft: IStoreSetupDraft): string {
  return draft.slugEdited
    ? draft.slug
    : followedSlug(draft.name, draft.location?.city ?? null);
}

/** A link `POST /restaurants` refused twice (409 `SLUG_TAKEN`), with the free links it offered. */
export interface ISlugConflict {
  slug: string;
  suggestions: string[];
}

/** Too short, not a valid link (reserved, bad characters), or known taken. An unchecked link never blocks. */
export function slugBlocksCreate(slug: string, state: TSlugState): boolean {
  return (
    !isValidFlowSlug(slug) ||
    state === 'short' ||
    state === 'invalid' ||
    state === 'taken'
  );
}

/**
 * What the link field shows: a link the create call refused twice reads
 * taken; a link the rules reject reads invalid while the server has not
 * answered (or could not).
 */
export function slugDisplayState(
  state: TSlugState,
  slug: string,
  conflict: ISlugConflict | null
): TSlugState {
  if (conflict && conflict.slug === slug) return 'taken';
  if (
    (state === 'checking' || state === 'unchecked') &&
    slug.length >= SLUG_MIN &&
    !isValidFlowSlug(slug)
  )
    return 'invalid';
  return state;
}

/** Up to 3 free links to adopt: the refused link's own offer, else the last check's. */
export function slugDisplaySuggestions(
  suggestions: string[],
  slug: string,
  conflict: ISlugConflict | null
): string[] {
  const list =
    conflict && conflict.slug === slug ? conflict.suggestions : suggestions;
  return list
    .filter((suggestion) => suggestion !== slug)
    .slice(0, MAX_SLUG_SUGGESTIONS);
}

/** The currency the store is created with: the chosen one, else the country's; Venezuela has none until chosen. */
export function draftCurrency(
  draft: Pick<IStoreSetupDraft, 'currency' | 'country'>
): string | null {
  return draft.currency ?? currencyForCountry(draft.country);
}

/** The 14 currencies of «Cambiar»: «Pesos argentinos ($)», named and sorted in the UI language. */
export function currencyOptions(locale: string): IPickerOption[] {
  return CURRENCIES.map((currency) => {
    const name = currencyPluralName(currency.value, locale);
    return {
      value: currency.value,
      label: `${name.charAt(0).toLocaleUpperCase(locale)}${name.slice(1)} (${
        currency.symbol
      })`,
    };
  }).sort((a, b) => a.label.localeCompare(b.label, locale));
}

export interface IBusinessReadiness {
  name: string;
  slug: string;
  slugState: TSlugState;
  currency: string | null;
  logoUploading: boolean;
}

/** «Crear mi tienda» is enabled (plan §4.2, bottom bar). */
export function canCreateStore(input: IBusinessReadiness): boolean {
  return (
    isValidStoreName(input.name) &&
    !slugBlocksCreate(input.slug, input.slugState) &&
    input.currency !== null &&
    !input.logoUploading
  );
}

/** The flow body of `POST /restaurants` (plan §6.2); null while the location or the currency is missing. */
export function buildCreateRestaurantRequest(
  draft: IStoreSetupDraft
): ICreateRestaurantRequest | null {
  const location = draft.location;
  const currency = draftCurrency(draft);
  if (!location || !currency) return null;
  const reference = location.reference.trim();
  return {
    name: draft.name.trim(),
    stringId: draftSlug(draft),
    country: draft.country,
    currency,
    lat: location.lat,
    lon: location.lng,
    address: location.address.trim(),
    province: location.province,
    city: location.city,
    locationId: null,
    userWhatsapp: null,
    setupSource: 'flow',
    idempotencyKey: draft.idempotencyKey,
    businessType: draft.businessType,
    placeId: location.placeId,
    addressReference: reference || null,
    logo: draft.logoPath,
    hours: draft.hours ? weekToRows(draft.hours) : [],
  };
}

export type THoursSource = 'google' | 'manual' | 'none';

export function hoursSource(
  draft: Pick<IStoreSetupDraft, 'hours' | 'hoursFromGoogle'>
): THoursSource {
  if (!draft.hours) return 'none';
  return draft.hoursFromGoogle ? 'google' : 'manual';
}

/** GA `store_created` params (§4.13). */
export interface IStoreCreatedEvent {
  country: string;
  type: TBusinessType;
  /** The type Google suggested was kept. */
  typePrefilled: boolean;
  slugEdited: boolean;
  hoursSource: THoursSource;
  hoursConfirmed: boolean;
  hasLogo: boolean;
}

export function storeCreatedEvent(draft: IStoreSetupDraft): IStoreCreatedEvent {
  return {
    country: draft.country,
    type: draft.businessType,
    typePrefilled: draft.businessTypeFromGoogle,
    slugEdited: draft.slugEdited,
    hoursSource: hoursSource(draft),
    hoursConfirmed: draft.hours !== null && draft.hoursConfirmed,
    hasLogo: draft.logoPath !== null,
  };
}

/**
 * Where an arrow key moves the focus inside a chip radiogroup (wrapping);
 * `Home`/`End` jump to the ends; null for any other key.
 */
export function radioKeyTarget(
  current: number,
  key: string,
  count: number
): number | null {
  if (count <= 0) return null;
  const from = current >= 0 && current < count ? current : 0;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return (from + 1) % count;
    case 'ArrowLeft':
    case 'ArrowUp':
      return (from - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}
